// src/features/agents/AgentsDirectory.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
  doc,
  onSnapshot as onDocSnap,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Users, MapPin, CircleDot, Star } from "lucide-react";
import { toast } from "react-hot-toast";

/* ───────── helpers ───────── */
const PLACEHOLDER_AVATAR = "/img/user.png";

function useUserProfile(uid) {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    if (!uid) return;
    const un = onDocSnap(doc(db, "users", uid), (s) =>
      setProfile(s.exists() ? { id: s.id, ...s.data() } : null)
    );
    return () => un();
  }, [uid]);
  return profile;
}

function useMyAgent(uid) {
  const [agentDoc, setAgentDoc] = useState(null);
  useEffect(() => {
    if (!uid) {
      setAgentDoc(null);
      return;
    }
    const un = onDocSnap(doc(db, "agents", uid), (s) =>
      setAgentDoc(s.exists() ? { id: s.id, ...s.data() } : null)
    );
    return () => un();
  }, [uid]);
  return agentDoc;
}

function AgentActiveCount({ agentId }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!agentId) return;
    const qy = query(
      collection(db, "cards"),
      where("agentUid", "==", agentId),
      where("status", "==", "active")
    );
    const un = onSnapshot(qy, (s) => setN(s.size));
    return () => un();
  }, [agentId]);
  return (
    <span className="text-xs px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-300">
      {n} active
    </span>
  );
}

function displayNameForAgent(agent, profile) {
  return (
    agent?.username ||
    agent?.fullName ||
    profile?.displayName ||
    profile?.username ||
    profile?.email ||
    "Agent"
  );
}
function avatarForProfile(profile) {
  return profile?.photoURL || profile?.avatar || PLACEHOLDER_AVATAR;
}

/* ───────── tile ───────── */
function AgentCard({ a, user, isFav, onToggleFav }) {
  const profile = useUserProfile(a.id);
  const avatar = avatarForProfile(profile);
  const name = displayNameForAgent(a, profile);
  const country = a.country || "—";
  const district = a.district || "—";
  const city = a.city || "—";

  return (
    <Link
      to={`/agents/${a.id}`}
      className="group block rounded-2xl bg-slate-800 hover:bg-slate-700 transition shadow-lg ring-1 ring-slate-700 overflow-hidden"
    >
      <div className="p-4 flex gap-4">
        <img
          src={avatar}
          alt={`${name} avatar`}
          className="w-14 h-14 rounded-full object-cover ring-2 ring-slate-600"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold truncate">{name}</div>
            <AgentActiveCount agentId={a.id} />
          </div>

          <div className="mt-1 text-xs text-slate-300 flex items-center gap-2">
            <MapPin size={14} className="opacity-70" />
            <span className="truncate">
              {city}, {district}, {country}
            </span>
          </div>

          {a.binancePayId && (
            <div className="mt-1 text-[11px] text-slate-400">
              Binance Pay: <span className="font-mono">{a.binancePayId}</span>
            </div>
          )}
        </div>

        {/* ⭐ toggle (stops navigation) */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleFav(a.id);
          }}
          title={isFav ? "Unfollow" : "Follow"}
          className={`shrink-0 p-2 rounded-xl ring-1 ring-slate-700 hover:bg-slate-700 transition ${
            isFav ? "text-yellow-300" : "text-slate-300"
          }`}
        >
          <Star size={18} fill={isFav ? "currentColor" : "none"} />
        </button>
      </div>

      <div className="px-4 pb-4">
        <div className="text-[11px] text-slate-400 flex items-center gap-2">
          <CircleDot size={12} className="opacity-60" />
          <span>
            Status: <b className="capitalize">{a.status || "approved"}</b>
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ───────── page ───────── */
export default function AgentsDirectory({ user }) {
  const [agents, setAgents] = useState([]);
  const [favIds, setFavIds] = useState(new Set());

  const myAgent = useMyAgent(user?.uid);

  // agents stream
  useEffect(() => {
    const qy = query(
      collection(db, "agents"),
      where("status", "==", "approved"),
      orderBy("country"),
      orderBy("district"),
      orderBy("createdAt", "desc")
    );
    const un = onSnapshot(qy, (snap) => {
      const arr = [];
      snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      setAgents(arr);
    });
    return () => un();
  }, []);

  // favorites stream
  useEffect(() => {
    if (!user?.uid) {
      setFavIds(new Set());
      return;
    }
    const qy = query(
      collection(db, "user_follows"),
      where("userUid", "==", user.uid)
    );
    const un = onSnapshot(qy, (snap) => {
      const s = new Set();
      snap.forEach((d) => s.add(d.data().agentUid));
      setFavIds(s);
    });
    return () => un?.();
  }, [user?.uid]);

  const toggleFav = async (agentId) => {
    if (!user?.uid) return toast("Please log in to follow agents.");
    try {
      const id = `${user.uid}_${agentId}`;
      const ref = doc(db, "user_follows", id);
      if (favIds.has(agentId)) {
        await deleteDoc(ref);
        toast.success("Removed from favorites");
      } else {
        await setDoc(ref, {
          userUid: user.uid,
          agentUid: agentId,
          createdAt: serverTimestamp(),
        });
        toast.success("Added to favorites");
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not update favorite");
    }
  };

  // compute favorites list + groups (favorites first inside each group)
  const favorites = useMemo(
    () => agents.filter((a) => favIds.has(a.id)),
    [agents, favIds]
  );

  const groups = useMemo(() => {
    const m = new Map();
    agents.forEach((a) => {
      const key = `${a.country || "Unknown"} › ${a.district || "—"}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(a);
    });
    for (const [k, list] of m.entries()) {
      m.set(
        k,
        [...list].sort((a, b) => {
          const af = favIds.has(a.id) ? 1 : 0;
          const bf = favIds.has(b.id) ? 1 : 0;
          if (af !== bf) return bf - af; // favorites first
          const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
          const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
          return bt - at;
        })
      );
    }
    return [...m.entries()];
  }, [agents, favIds]);

  return (
    <div className="max-w-6xl mx-auto mt-8 md:mt-16">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Users className="text-lime-400" />
          <h1 className="text-2xl font-bold">Agents Directory</h1>
        </div>
        {user?.uid && (
          <div className="text-xs text-slate-400">
            Favorites appear first <span className="text-yellow-300">⭐</span>
          </div>
        )}
      </div>

      {/* pinned favorites */}
      {user?.uid && favorites.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm uppercase tracking-wider text-slate-300 mb-3">
            Your favorites
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {favorites.map((a) => (
              <AgentCard
                key={`fav-${a.id}`}
                a={a}
                user={user}
                isFav={favIds.has(a.id)}
                onToggleFav={toggleFav}
              />
            ))}
          </div>
        </section>
      )}

      {/* grouped by country › district */}
      {groups.map(([key, list]) => (
        <section key={key} className="mb-8">
          <h2 className="text-sm uppercase tracking-wider text-slate-400 mb-3">
            {key}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((a) => (
              <AgentCard
                key={a.id}
                a={a}
                user={user}
                isFav={favIds.has(a.id)}
                onToggleFav={toggleFav}
              />
            ))}
          </div>
        </section>
      ))}

      {!agents.length && (
        <div className="p-6 rounded-2xl bg-slate-800 text-slate-300">
          No approved agents yet.
        </div>
      )}

      {user?.uid && myAgent ? (
        <div className="fixed bottom-6 right-6 z-50">
          <Link
            to="/agent/apply"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-full shadow-xl
                 bg-gradient-to-r from-indigo-400 to-blue-400 text-black font-bold
                 hover:from-indigo-300 hover:to-blue-300 transition"
            title="Edit your agent profile"
          >
            <span className="text-lg leading-none">⚙️</span>
            <span>Edit Agent Profile</span>
          </Link>
        </div>
      ) : (
        <div className="fixed bottom-6 right-6 z-50">
          <Link
            to="/agent/apply"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-full shadow-xl
                 bg-gradient-to-r from-lime-400 to-emerald-400 text-black font-bold
                 hover:from-lime-300 hover:to-emerald-300 transition"
            title="Apply to become an agent"
          >
            <span className="text-lg leading-none">＋</span>
            <span>Apply as Agent</span>
          </Link>
        </div>
      )}
    </div>
  );
}
